/**
 * StandardErrorState Component
 *
 * Unified error state component with retry functionality.
 * Used across all applications for consistent error UX.
 *
 * @example
 * <StandardErrorState
 *   title="Failed to load data"
 *   message="An error occurred while loading the data."
 *   onRetry={handleRetry}
 * />
 */

import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { StandardButton } from './StandardButton';

export interface StandardErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  showIcon?: boolean;
  fullScreen?: boolean;
}

export const StandardErrorState: React.FC<StandardErrorStateProps> = ({
  title = 'Something went wrong',
  message = 'An error occurred. Please try again.',
  onRetry,
  retryLabel = 'Try Again',
  showIcon = true,
  fullScreen = false
}) => {
  const content = (
    <div className="text-center max-w-md mx-auto">
      {showIcon && (
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3">
            <AlertCircle className="text-red-600 dark:text-red-400" size={32} />
          </div>
        </div>
      )}

      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
        {title}
      </h3>

      <p className="text-gray-600 dark:text-gray-400 mb-6">
        {message}
      </p>

      {onRetry && (
        <StandardButton
          variant="primary"
          onClick={onRetry}
          leftIcon={<RefreshCw size={16} />}
        >
          {retryLabel}
        </StandardButton>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center p-4">
        {content}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-12 px-4">
      {content}
    </div>
  );
};
